<template>
  <nav class="sticky top-0 z-50 w-full bg-burgundy-900 font-artex">
    <div class="mx-auto w-full px-2 xl:container">
      <div class="flex h-16 items-center justify-between">
        <div class="relative flex items-center">
          <RouterLink
            :to="{ name: homeRouteName }"
            aria-label="Home"
            class="-mt-0.5 mr-8 ml-4 flex gap-2 lg:ml-0"
          >
            <img alt="" src="@/assets/Logomark.png" class="m-auto size-6" />
            <p class="font-artex text-lg text-white">Fyr</p>
          </RouterLink>
          <div v-if="authStore.isAuthed" class="hidden lg:block">
            <div class="ml-2 flex space-x-1 lg:space-x-4">
              <NavbarLinkDropdown
                :links="analyticsRoutes"
                text="Analytics"
                :max-rows="3"
              />
              <NavbarLinkDropdown
                :links="insightsRoutes"
                text="Insights"
                :direction="bp.greater.lg ? 'right' : 'left'"
                :disabled-routes="disabledRouteNames"
              />
              <NavbarLinkDropdown
                :links="computedToolsRoutes"
                text="Content lab"
                :direction="bp.greater.lg ? 'right' : 'left'"
              />
              <NavbarLinkDropdown
                :links="salesRoutes"
                text="Budget"
                :direction="bp.greater.lg ? 'right' : 'left'"
                :disabled-routes="disabledRouteNames"
              />
              <NavbarLinkDropdown :links="reportingRoutes" text="Reporting" />
            </div>
          </div>
        </div>
        <div class="hidden lg:block">
          <div v-if="authStore.isAuthed" class="flex items-center gap-x-1">
            <SelectOrganizationDropdown v-if="showSelectOrganizationDropdown" />
            <NotificationDropdown />
            <NavbarLinkDropdown
              :links="filteredSettingsRoutes"
              text=""
              direction="left"
              :max-rows="filteredSettingsRoutes.length + 2"
              :disabled-routes="disabledRouteNames"
            >
              <!-- <NavbarLinkDropdown :links="settingRoutes" text="" direction="left"> -->
              <template #avatar>
                <Avatar :user="self.data" />
              </template>
              <template #extra-items>
                <MenuItem>
                  <a
                    href="https://community.fyr.ai/hc/en-us"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex w-full px-4 pt-2 pb-3 transition duration-300 hover:cursor-pointer hover:bg-burgundy-50"
                    @click="onHelpCenterClickNavbar"
                  >
                    <Icon
                      name="book-arrow-right"
                      class="mt-1 size-6 text-gray-600"
                    />
                    <span class="ml-4 w-full text-left leading-tight">
                      <p>Help Center & Support</p>
                      <p class="mt-1 text-sm text-gray-700">
                        Open support documentation
                      </p>
                    </span>
                  </a>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <button
                    class="flex w-full rounded-b-md px-4 pt-2 pb-3 transition duration-300 hover:cursor-pointer hover:bg-burgundy-50"
                    :class="{ 'bg-burgundy-50 outline-none': active }"
                    @click="logOut()"
                  >
                    <Icon
                      name="house-leave-solid"
                      class="mt-1 size-6 text-gray-600"
                    />
                    <span class="ml-4 w-full text-left leading-tight">
                      <p>Sign out</p>
                      <p class="mt-1 text-sm text-gray-700">
                        Back to the login screen
                      </p>
                    </span>
                  </button>
                </MenuItem>
              </template>
            </NavbarLinkDropdown>
          </div>
        </div>

        <div v-if="authStore.isAuthed" class="mr-2 flex space-x-4 lg:hidden">
          <!-- Mobile menu button -->
          <RouterLink
            :to="{ name: 'notifications' }"
            class="relative inline-flex items-center justify-center rounded-md p-1 text-gray-400 hover:bg-burgundy-700 hover:text-white focus:bg-burgundy-700 focus:text-white focus:outline-none"
          >
            <Icon name="bell" class="size-5" />
            <div
              v-if="hasUnreadNotifications"
              class="absolute top-0.5 right-0.5 size-2 rounded-full bg-red-600"
            />
            <div
              v-if="hasUnreadNotifications"
              class="absolute top-0.5 right-0.5 size-2 animate-ping rounded-full bg-red-600"
            />
            <span class="sr-only">Unread notifications</span>
          </RouterLink>
          <button
            class="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-burgundy-700 hover:text-white focus:bg-burgundy-700 focus:text-white focus:outline-none"
            :aria-label="showMobileMenu ? 'Hide menu' : 'Show menu'"
            :aria-expanded="showMobileMenu"
            aria-controls="mobile-menu"
            @click="toggleMobileMenu"
          >
            <!-- Menu open: "hidden", Menu closed: "block" -->
            <Icon v-if="!showMobileMenu" name="bars" class="size-5" />
            <!-- Menu open: "block", Menu closed: "hidden" -->
            <Icon v-if="showMobileMenu" name="times" class="size-5" />
          </button>
        </div>
      </div>
    </div>

    <!--
      Mobile menu, toggle classes based on menu state.
      Open: "block", closed: "hidden"
    -->
    <div
      v-if="authStore.isAuthed"
      id="mobile-menu"
      class="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto bg-burgundy-900 lg:hidden"
      :class="{ block: showMobileMenu, hidden: !showMobileMenu }"
    >
      <div class="space-y-1 px-2 pt-2 pb-3 sm:px-3">
        <div class="ml-2 flex flex-col px-1 lg:flex-row lg:space-x-4 lg:px-0">
          <NavbarLinkDropdown
            :links="analyticsRoutes"
            text="Analytics"
            @hide-menu="hideMobileMenu"
          />
          <NavbarLinkDropdown
            :links="insightsRoutes"
            text="Insights"
            :direction="bp.greater.lg ? 'right' : 'left'"
            :disabled-routes="disabledRouteNames"
            @hide-menu="hideMobileMenu"
          />
          <NavbarLinkDropdown
            :links="computedToolsRoutes"
            text="Content lab"
            :direction="bp.greater.lg ? 'right' : 'left'"
            @hide-menu="hideMobileMenu"
          />
          <NavbarLinkDropdown
            :links="salesRoutes"
            text="Budget"
            :direction="bp.greater.lg ? 'right' : 'left'"
            :disabled-routes="disabledRouteNames"
            @hide-menu="hideMobileMenu"
          />
          <NavbarLinkDropdown
            :links="reportingRoutes"
            text="Reporting"
            @hide-menu="hideMobileMenu"
          />
        </div>
      </div>
      <div v-if="showSelectOrganizationDropdown" class="px-5 pt-4" @click.stop>
        <p class="pl-3 text-xs text-gray-200">Switch organization</p>
        <SelectOrganizationDropdown mobile />
      </div>
      <div class="mt-2 border-gray-700 pt-4 pb-3 lg:mt-0 lg:border-t">
        <div
          class="flex items-center space-x-3 px-5"
          @click="profileDropdown = !profileDropdown"
        >
          <div class="shrink-0">
            <Avatar :user="self.data" />
          </div>
          <div class="space-y-1">
            <div class="flex leading-none text-white">
              {{ self.data?.name }}
              <Icon
                v-if="showMobileMenu"
                name="angle-down"
                class="-mb-0.5 ml-1 size-4 self-center transition"
                :class="{ 'rotate-180': profileDropdown }"
              />
            </div>
            <div class="text-sm leading-none text-gray-400">
              {{ self.data?.email }}
            </div>
          </div>
        </div>
        <div v-if="profileDropdown" class="mt-3 ml-2 space-y-1 px-2 lg:ml-0">
          <RouterLink
            :to="{ name: 'profile' }"
            active-class="bg-gray-900"
            class="block rounded-md px-3 py-1 text-white opacity-80 hover:bg-gray-700 focus:bg-gray-700 focus:text-white focus:outline-none lg:text-gray-200"
            @click="hideMobileMenu"
          >
            Profile
          </RouterLink>
          <RouterLink
            :to="{ name: 'settings' }"
            active-class="bg-purple-900"
            class="block rounded-md px-3 py-1 text-gray-200 hover:bg-purple-700 focus:bg-purple-700 focus:text-white focus:outline-none"
            @click="hideMobileMenu"
          >
            Organization settings
          </RouterLink>
          <button
            class="block w-full rounded-md px-3 py-1 text-left text-gray-200 hover:bg-gray-700 focus:bg-gray-700 focus:text-white focus:outline-none"
            @click="logOut"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import NavbarLinkDropdown from "@/components/Navigation/NavbarLinkDropdown.vue";
import Avatar from "@/components/Core/Avatar.vue";
import Icon from "@/components/Core/Icon.vue";
import { marketingRoutes } from "@/router/marketing_routes";
import { commerceRoutes } from "@/router/commerce_routes";
import { insightsRoutes } from "@/router/insights_routes";
import { salesRoutes } from "@/router/sales_routes";
import { settingRoutes } from "@/router/setting_routes";
import { toolsRoutes } from "@/router/tools_routes";
import { useAuthStore } from "@/store/auth";
import NotificationDropdown from "@/components/Core/NotificationDropdown.vue";
import { useNotificationsQuery } from "@/services/notifications";
import { useOrganizationsQuery } from "@/services/organization";
import { homeRouteNameOrFeed } from "@/router";
import { useProfileQuery } from "@/services/user";
import { useBp } from "@/utils/breakpoints";
import { reportingRoutes } from "@/router/reporting_routes";
import { RouteRecordRaw, useRouter } from "vue-router";
import SelectOrganizationDropdown from "@/components/Core/SelectOrganizationDropdown.vue";
import { MenuItem } from "@headlessui/vue";
import { useFeatureFlagStatus } from "@/services/feature_flags";

const bp = useBp();
const authStore = useAuthStore();
const self = useProfileQuery();
const router = useRouter();

// The logo must always navigate somewhere: a saved preference pointing at a
// param'd route would otherwise make RouterLink resolve nothing and swallow
// the click.
const homeRouteName = computed(() =>
  homeRouteNameOrFeed(router, self.data?.selectedHomeRoute),
);
const showMobileMenu = ref(false);
const profileDropdown = ref(false);

const analyticsRoutes = [...marketingRoutes, ...commerceRoutes];
function toggleMobileMenu(): void {
  showMobileMenu.value = !showMobileMenu.value;
}

if (window.innerWidth > 768) {
  profileDropdown.value = true;
}
function hideMobileMenu(): void {
  showMobileMenu.value = false;
}

const notifications = useNotificationsQuery();
const hasUnreadNotifications = computed(() => {
  return notifications.data?.some((n) => n.readStatus === "UNREAD") ?? false;
});
function logOut(): void {
  authStore.logout();
}
const organizations = useOrganizationsQuery();

// Hub gating follows the user's home agency (resolved server-side and gated
// on membership), not the active org selection — so the Hub link stays
// visible no matter which org is selected, as long as the user belongs to an
// agency.
const homeAgency = computed(() => self.data?.homeAgency ?? null);

// Belongs to at least one agency (controls whether the Hub slot applies).
const isCurrentAgencyUser = computed(() => homeAgency.value !== null);

// Has at least one flag-enabled member agency — the actual Hub-availability
// signal, matching the backend (which serves the hub if ANY member agency is
// enabled, not only the home agency).
const hasAgencyHub = computed(() => self.data?.hasAgencyHub === true);

// `budgetTool` (camelCase) — the feature-flags API runs through DRF's
// CamelCaseJSONRenderer, so client lookups have to match the rendered key
// even though the backend stores `budget_tool`. Matches the
// `aiContentGeneration` lookup below.
const { isVisible: budgetToolVisible } = useFeatureFlagStatus("budgetTool");

// Route names that should be shown but disabled (greyed out with "In Beta").
// An agency member whose hub isn't enabled anywhere yet sees the greyed link.
// (AI Visibility now lives as tabs under Competitor Monitoring, gated there.)
const disabledRouteNames = computed(() => {
  const names = new Set<string>();
  if (isCurrentAgencyUser.value && !hasAgencyHub.value) {
    names.add("hub");
  }
  if (!budgetToolVisible.value) {
    names.add("budget_tool");
  }
  return names;
});

const filteredSettingsRoutes = computed(() => {
  return settingRoutes.filter((route: RouteRecordRaw) => {
    if (route.meta && "requiredPermissions" in route.meta) {
      if (self.data) {
        return route.meta.requiredPermissions?.every((permission) => {
          // Check user profile permissions first
          if (permission in self.data) {
            return self.data[permission as keyof typeof self.data];
          }
          if (permission === "isAgency") {
            return isCurrentAgencyUser.value;
          }
          return false;
        });
      }
    } else {
      return true;
    }
  });
});

const {
  isVisible: contentLabVisible,
  consentPending: contentLabConsentPending,
} = useFeatureFlagStatus("aiContentGeneration");

const computedToolsRoutes = computed<RouteRecordRaw[]>(() =>
  toolsRoutes
    .filter((route) => {
      if (route.name === "create-content" && !contentLabVisible.value) {
        return false;
      }
      return true;
    })
    .map((route) => {
      if (route.name === "create-content" && contentLabConsentPending.value) {
        return {
          ...route,
          meta: { ...route.meta, consentPending: true },
        } as RouteRecordRaw;
      }
      return route;
    }),
);

const showSelectOrganizationDropdown = computed(
  () => organizations.data && organizations.data.length > 1,
);

import { useTrack } from "@/services/gtm";
const track = useTrack();

function onHelpCenterClickNavbar(): void {
  track("click_help_center_navbar");
}
</script>
